import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'all',      label: 'Όλα' },
  { id: 'styling',  label: 'Προϊόντα Styling' },
  { id: 'beard',    label: 'Περιποίηση Γενειάδας' },
  { id: 'shampoo',  label: 'Σαμπουάν' },
];

const CATEGORY_LABELS = {
  styling: 'Προϊόντα Styling',
  beard:   'Περιποίηση Γενειάδας',
  shampoo: 'Σαμπουάν',
};

const EMPTY_FORM = { name: '', description: '', price: '', image_url: '', categories: ['styling'] };

// Normalize: support old single 'category' field and new 'categories' array
const getCategories = (p) => {
  if (p.categories && p.categories.length > 0) return p.categories;
  if (p.category) return [p.category];
  return ['styling'];
};

const ProductsPage = ({ isAdmin }) => {
  const [products, setProducts]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [editMode, setEditMode]       = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [formData, setFormData]       = useState(EMPTY_FORM);
  const [deleteId, setDeleteId]       = useState(null);

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, price: parseFloat(formData.price) };
      if (editMode && currentProduct) {
        await axios.put(`${API}/products/${currentProduct.product_id}`, data, { withCredentials: true });
      } else {
        await axios.post(`${API}/products`, data, { withCredentials: true });
      }
      setDialogOpen(false);
      setFormData(EMPTY_FORM);
      setEditMode(false);
      setCurrentProduct(null);
      fetchProducts();
    } catch (e) {
      console.error(e);
    }
  };

  const handleEdit = (p) => {
    setCurrentProduct(p);
    setFormData({ name: p.name, description: p.description, price: p.price.toString(), image_url: p.image_url || '', categories: getCategories(p) });
    setEditMode(true);
    setDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/products/${deleteId}`, { withCredentials: true });
      fetchProducts();
    } catch (e) {
      console.error(e);
    } finally {
      setDeleteId(null);
    }
  };

  const toggleCategory = (catId) => {
    setFormData(prev => {
      const has = prev.categories.includes(catId);
      if (has && prev.categories.length === 1) return prev; // τουλάχιστον 1
      return { ...prev, categories: has ? prev.categories.filter(c => c !== catId) : [...prev.categories, catId] };
    });
  };

  const filtered = activeCategory === 'all'
    ? products
    : products.filter(p => getCategories(p).includes(activeCategory));

  const grouped = CATEGORIES.slice(1).map(cat => ({
    ...cat,
    items: products.filter(p => getCategories(p).includes(cat.id))
  })).filter(g => g.items.length > 0);

  if (loading) return <div className="text-center text-gray-400 py-12">Φόρτωση...</div>;

  return (
    <div className="space-y-8" data-testid="products-page">

      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-light tracking-wide">Προϊόντα</h2>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setFormData(EMPTY_FORM);
              setEditMode(false);
              setCurrentProduct(null);
            }
          }}>
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  setFormData(EMPTY_FORM);
                  setEditMode(false);
                }}
                data-testid="create-product-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Νέο Προϊόν
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg bg-black border-2 border-white">
              <DialogHeader>
                <p className="premium-modal-eyebrow">DIMAKAS Products</p>
                <DialogTitle>
                  {editMode ? 'Επεξεργασία Προϊόντος' : 'Νέο Προϊόν'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Multi-category selector */}
                <div className="space-y-2">
                  <label className="text-xs text-gray-400 tracking-widest uppercase">Κατηγορίες <span className="normal-case text-gray-500">(επιλέξτε όσες θέλετε)</span></label>
                  <div className="grid grid-cols-3 gap-2">
                    {CATEGORIES.slice(1).map(cat => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => toggleCategory(cat.id)}
                        className={`py-2 px-3 text-xs tracking-wide border transition-all ${
                          formData.categories.includes(cat.id)
                            ? 'text-black border-[#E8D48B] bg-gradient-to-r from-[#8B6914] via-[#A9B2B9] to-[#E8D48B]'
                            : 'bg-transparent text-gray-400 border-white/20 hover:border-[#A9B2B9]/40 hover:text-[#A9B2B9]'
                        }`}
                        data-testid={`category-btn-${cat.id}`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>

                <Input placeholder="Όνομα Προϊόντος" value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  required className="premium-modal-input border-white/15 text-white placeholder:text-gray-500"
                  data-testid="product-name-input" />
                <Textarea placeholder="Περιγραφή" value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  required rows={3} className="premium-modal-input min-h-[110px] border-white/15 text-white placeholder:text-gray-500" />
                <Input type="number" step="0.01" placeholder="Τιμή (€)" value={formData.price}
                  onChange={e => setFormData({ ...formData, price: e.target.value })}
                  required className="premium-modal-input border-white/15 text-white placeholder:text-gray-500" />
                <Input placeholder="URL Εικόνας (προαιρετικό)" value={formData.image_url}
                  onChange={e => setFormData({ ...formData, image_url: e.target.value })}
                  className="premium-modal-input border-white/15 text-white placeholder:text-gray-500" />

                <div className="flex justify-end space-x-2 pt-1">
                  <Button type="button" variant="outline" className="premium-modal-secondary" onClick={() => setDialogOpen(false)}>Ακύρωση</Button>
                  <Button type="submit" className="premium-modal-primary" data-testid="save-product-btn">Αποθήκευση</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Category Filter Tabs */}
      <div className="flex space-x-1 border-b border-white/10">
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`px-5 py-2.5 text-sm font-light tracking-wide transition-all border-b-2 -mb-px ${
              activeCategory === cat.id
                ? 'border-white text-white'
                : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}
            data-testid={`filter-${cat.id}`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Products */}
      {activeCategory === 'all' ? (
        // Grouped view
        grouped.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-10">
            {grouped.map(group => (
              <div key={group.id}>
                <h3 className="text-lg font-light tracking-widest uppercase text-gray-400 mb-4 border-b border-white/10 pb-3">
                  {group.label}
                </h3>
                <ProductGrid products={group.items} isAdmin={isAdmin} onEdit={handleEdit} onDelete={setDeleteId} />
              </div>
            ))}
          </div>
        )
      ) : (
        filtered.length === 0 ? <EmptyState /> : (
          <ProductGrid products={filtered} isAdmin={isAdmin} onEdit={handleEdit} onDelete={setDeleteId} />
        )
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={open => { if (!open) setDeleteId(null); }}>
        <AlertDialogContent className="max-w-md bg-black border-2 border-white">
          <AlertDialogHeader>
            <p className="premium-modal-eyebrow">DIMAKAS Products</p>
            <AlertDialogTitle>Διαγραφή Προϊόντος</AlertDialogTitle>
            <AlertDialogDescription>
              Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το προϊόν;
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="premium-modal-danger">Διαγραφή</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ProductGrid({ products, isAdmin, onEdit, onDelete }) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
      {products.map(product => (
        <Card key={product.product_id} className="bg-white/5 border-white/10 card-lift overflow-hidden" data-testid={`product-${product.product_id}`}>
          {product.image_url && (
            <div className="aspect-square overflow-hidden">
              <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
            </div>
          )}
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-lg font-light">{product.name}</CardTitle>
            <div className="flex flex-wrap gap-1 mt-1">
              {getCategories(product).map(c => (
                <span key={c} className="text-xs text-gray-500 tracking-widest uppercase border border-white/10 px-2 py-0.5">
                  {CATEGORY_LABELS[c] || c}
                </span>
              ))}
            </div>
          </CardHeader>
          <CardContent className="pb-2">
            <p className="text-gray-400 text-sm mb-3 font-light">{product.description}</p>
          </CardContent>
          {isAdmin && (
            <CardFooter className="flex justify-end space-x-2 pt-0">
              <Button variant="ghost" size="sm" onClick={() => onEdit(product)} data-testid={`edit-product-${product.product_id}`}>
                <Edit className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onDelete(product.product_id)} data-testid={`delete-product-${product.product_id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </CardFooter>
          )}
        </Card>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center text-gray-500 py-16 font-light">
      Δεν υπάρχουν προϊόντα σε αυτή την κατηγορία
    </div>
  );
}

export default ProductsPage;
